import {Link, Navigate, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import type {Category} from "@zaaaz/shared";
import {listProducts, type Product, type I18nKey} from "@zaaaz/shared";
import {getCategoriesCached} from "@/lib/shopCache";
import {normalizeShopHref} from "@/lib/paths";
import {categoryBannerImage} from "@/components/shop/categoryBannerImages";
import {BEAUTY_DEPARTMENT_ID, getDepartmentById} from "@/components/shop/homeDepartments";
import {HomePerfumeSpotlight} from "@/components/shop/HomePerfumeSpotlight";
import {HomeBrandsMarquee} from "@/components/shop/HomeBrandsMarquee";
import {DepartmentHubStrip} from "@/components/shop/DepartmentHubStrip";
import {ProductCard} from "@/components/shop/ProductCard";
import {useI18n} from "@/components/shop/I18nProvider";

type CategoryTile = {
  id: number;
  nume: string;
  url: string;
  image: string | null;
};

function buildCategoryTiles(cats: Category[]): CategoryTile[] {
  return cats.map((category) => ({
    id: category.id,
    nume: category.nume,
    url: normalizeShopHref(category.url),
    image: categoryBannerImage(category.slug, category.id)
  }));
}

function HubProductRow({list, titleKey, moreUrl}: {list: string; titleKey: I18nKey; moreUrl: string}) {
  const {t} = useI18n();
  const [items, setItems] = useState<Product[]>([]);
  useEffect(() => {
    listProducts({list, pageSize: 8}).then((r) => setItems(r.items)).catch(() => {});
  }, [list]);
  if(!items.length) return null;
  return (
    <section className="bx-homeProducts cc-deptHub__products">
      <div className="cc-ds-sectionHeader bx-homeProducts__head">
        <h2 className="cc-ds-sectionHeader__title">{t(titleKey)}</h2>
        <Link to={moreUrl} className="cc-ds-sectionHeader__link">{t("More products")}</Link>
      </div>
      <div className="cc-productGrid">
        {items.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
}

export function DepartmentHubPage() {
  const {departmentId = ""} = useParams();
  const {t} = useI18n();
  const dept = getDepartmentById(departmentId);
  const [categories, setCategories] = useState<CategoryTile[]>([]);

  useEffect(() => {
    if(dept?.id !== BEAUTY_DEPARTMENT_ID) return;
    getCategoriesCached()
      .then((cats) => setCategories(buildCategoryTiles(cats)))
      .catch(() => {});
  }, [dept?.id]);

  if(!dept || dept.status !== "active") return <Navigate to="/" replace />;

  return (
    <div className="cc-deptHub">
      <header className="cc-deptHub__hero" style={{backgroundColor: dept.cardBg}}>
        <div className="cc-deptHub__heroInner">
          <p className="cc-deptHub__eyebrow">Zaaaz</p>
          <h1 className="cc-deptHub__title">{t(dept.nameKey)}</h1>
          <p className="cc-deptHub__intro">{t("Beauty hub intro")}</p>
        </div>
        {dept.cardImage ? (
          <img src={dept.cardImage} alt="" className="cc-deptHub__heroPhoto" loading="eager" decoding="async" />
        ) : null}
      </header>

      {categories.length ? (
        <section className="cc-deptHub__categories" aria-labelledby="beauty-hub-categories">
          <h2 id="beauty-hub-categories" className="cc-deptHub__sectionTitle">{t("Beauty hub categories title")}</h2>
          <div className="cc-deptHub__catGrid">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={cat.url}
                className={`cc-deptHub__catCard anim${cat.image ? "" : " cc-deptHub__catCard--noImg"}`}
                style={{backgroundColor: dept.cardBg}}
              >
                <span className="cc-deptHub__catName">{cat.nume}</span>
                {cat.image ? (
                  <div className="cc-deptHub__catVisual">
                    <img src={cat.image} alt="" className="cc-deptHub__catImg" loading="lazy" decoding="async" />
                  </div>
                ) : null}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <HomePerfumeSpotlight />
      <HomeBrandsMarquee />
      <HubProductRow list="promotii" titleKey="LIMITED TIME DISCOUNTS" moreUrl="/promotions" />
      <DepartmentHubStrip activeId={dept.id} />
    </div>
  );
}
